// Options du projet : les objectifs, l'équipe (qui accède au projet), les
// sources et connecteurs de mesure, l'agenda et les alertes. La taxonomie a
// quitté les options pour le menu de gauche (TaxonomyScreen).
//
// La gestion de l'équipe est réservée aux ADMINS d'organisation — le serveur le
// garantit (403). Les autres membres voient l'équipe en lecture : savoir avec
// qui l'on travaille n'est pas un secret, décider qui voit les verbatims l'est.
import { useState, type FormEvent } from 'react';
import {
  useInviteMember,
  useProjectTeam,
  useRemoveMember,
  useRevokeTeamInvitation,
  useRotateTeamInvitation,
  useSetMemberRole,
  useAcceptAccessRequest,
  useDeclineAccessRequest,
} from '../api/queries';
import { ApiRequestError } from '../api/client';
import type { ProjectRole, ProjectTeamMember, TeamInvitation } from '../api/types';
import { Button, buttonClass, ErrorBanner, Eyebrow, Field, Note, Select, Spinner, Avatar } from '../components/ui';
import { hrefFor } from '../router';
import { ConnectorsSection } from './ConnectorsSection';
import { WidgetSection } from './WidgetSection';
import { ObjectivesSection } from './ObjectivesSection';
import { CalendarSection } from '../calendar/CalendarSection';
import { AlertSettingsForm } from '../dashboard/EvolutionPanel';
import { TabBar, TabPanel, useTabs } from '../components/Tabs';
import '../calendar/calendar.css';
import './options.css';

const ROLES: { value: ProjectRole; label: string; hint: string }[] = [
  { value: 'Responsable', label: 'Responsable', hint: 'Lit et écrit : études, imports, insights, mesure 0.' },
  { value: 'Contributeur', label: 'Contributeur', hint: 'Lit tout le projet ; n’écrit pas.' },
  { value: 'Observateur', label: 'Observateur', hint: 'Consulte le projet, en lecture seule.' },
];

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiRequestError) return cause.message;
  return 'Une erreur inattendue est survenue.';
}

const OPTION_TABS = ['objectifs', 'equipe', 'sources', 'agenda', 'alertes'] as const;

export function OptionsScreen({ projectId, projectName }: { projectId: string; projectName: string }) {
  // Un réglage à la fois : un onglet par sujet.
  const [tab, setTab] = useTabs('options', OPTION_TABS, 'objectifs');
  return (
    <div className="app-main options">
      <div className="zy-page-head__texte">
        <Eyebrow>Projet · {projectName}</Eyebrow>
        <h1 className="t-h1">Options</h1>
        <p className="zy-intro">Ce que la recherche doit servir, comment on range la matière, qui accède au projet, d’où viennent les mesures, et votre agenda.</p>
      </div>
      <TabBar
        label="Réglages du projet"
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'objectifs', label: 'Objectifs' },
          { id: 'equipe', label: 'Équipe' },
          { id: 'sources', label: 'Sources et connecteurs' },
          { id: 'agenda', label: 'Mon agenda' },
          { id: 'alertes', label: 'Alertes' },
        ]}
      />
      <TabPanel id={tab}>
        {tab === 'objectifs' ? <ObjectivesSection projectId={projectId} /> : null}
        {tab === 'equipe' ? <TeamSection projectId={projectId} projectName={projectName} /> : null}
        {tab === 'sources' ? <><ConnectorsSection projectId={projectId} /><WidgetSection projectId={projectId} /></> : null}
        {tab === 'agenda' ? <CalendarSection /> : null}
        {tab === 'alertes' ? (
          <section className="zy-card opt-section" aria-labelledby="alerts-title">
            <div className="opt-head">
              <div>
                <Eyebrow>Alertes</Eyebrow>
                <h2 className="t-h3" id="alerts-title">La mesure automatique de l’adoption</h2>
                <p className="card-lede">
                  À la fréquence choisie, l’adoption est recalculée et photographiée ; vous êtes prévenu de ce qui
                  bouge. L’évolution se lit au tableau de bord, onglet « Évolution ».
                </p>
              </div>
            </div>
            <AlertSettingsForm projectId={projectId} />
          </section>
        ) : null}
      </TabPanel>
    </div>
  );
}

// --- Équipe ------------------------------------------------------------------

function TeamSection({ projectId, projectName }: { projectId: string; projectName: string }) {
  const team = useProjectTeam(projectId);
  const [requestLink, setRequestLink] = useState<{ email: string; name: string | null; link: string } | null>(null);

  return (
    <section className="zy-card opt-section" aria-labelledby="team-title">
      <div className="opt-head">
        <div>
          <Eyebrow>Équipe du projet</Eyebrow>
          <h2 className="t-h3" id="team-title">Qui accède à ce projet</h2>
          <p className="card-lede">
            Les administrateurs de l’organisation accèdent à tous les projets. Les autres personnes
            n’accèdent qu’aux projets où elles sont ajoutées, avec un rôle.
          </p>
        </div>
      </div>

      {team.isPending ? (
        <Spinner label="Chargement de l’équipe…" />
      ) : team.isError ? (
        <ErrorBanner>{errorMessage(team.error)}</ErrorBanner>
      ) : (
        <>
          <ul className="team-list">
            {team.data.members.map((m) => (
              <MemberRow key={m.userId} projectId={projectId} member={m} canManage={team.data.canManage} />
            ))}
          </ul>

          {team.data.canManage ? (
            <>
              {/* Le lien d'une demande acceptée vit ICI, pas dans sa ligne : la
                  demande quitte la liste dès qu'elle est acceptée, et le lien
                  — lisible une seule fois — partirait avec elle. */}
              {requestLink ? (
                <FreshInvite {...requestLink} projectName={projectName} onDone={() => setRequestLink(null)} />
              ) : null}
              {team.data.accessRequests.length > 0 ? (
                <div className="team-invites team-requests">
                  <p className="opt-subtitle">Demandes d’accès · {team.data.accessRequests.length}</p>
                  <p className="m0-note">
                    Des répondants au questionnaire de mesure 0 souhaitent suivre la recherche. Accepter leur
                    envoie une invitation ; un accès donne à voir les verbatims du projet.
                  </p>
                  <ul>
                    {team.data.accessRequests.map((r) => (
                      <AccessRequestRow key={r.id} projectId={projectId} request={r} onInvited={setRequestLink} />
                    ))}
                  </ul>
                </div>
              ) : null}
              <AddMember projectId={projectId} candidates={team.data.candidates} />
              <InviteMember projectId={projectId} projectName={projectName} />
              {team.data.invitations.length > 0 ? (
                <div className="team-invites">
                  <p className="opt-subtitle">Invitations en attente</p>
                  <ul>
                    {team.data.invitations.map((inv) => (
                      <TeamInvitationRow key={inv.id} projectId={projectId} projectName={projectName} invitation={inv} />
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          ) : (
            <Note tone="lecture">Lecture seule — la gestion de l’équipe est réservée aux administrateurs.</Note>
          )}
        </>
      )}
    </section>
  );
}

function MemberRow({ projectId, member, canManage }: { projectId: string; member: ProjectTeamMember; canManage: boolean }) {
  const setRole = useSetMemberRole(projectId);
  const remove = useRemoveMember(projectId);
  const [confirm, setConfirm] = useState(false);
  const error = setRole.error ?? remove.error;

  return (
    <li className="team-row">
      <Avatar initials={member.initials ?? '?'} seed={member.userId} size="m" />
      <div className="team-who">
        <p className="team-name">
          {member.name}
          {!member.active ? <span className="team-flag">compte inactif</span> : null}
        </p>
        <p className="team-email">{member.email}</p>
      </div>

      {member.access === 'admin' ? (
        <span className="team-role team-role-admin" title="Accès à tous les projets de l’organisation">
          Admin · accès complet
        </span>
      ) : canManage ? (
        <div className="team-actions">
          <label className="sr-only" htmlFor={`role-${member.userId}`}>Rôle de {member.name}</label>
          <Select
            id={`role-${member.userId}`}
            className="team-select"
            value={member.role}
            disabled={setRole.isPending}
            onChange={(e) => setRole.mutate({ userId: member.userId, role: e.target.value as ProjectRole })}
          >
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </Select>
          {confirm ? (
            <span className="team-confirm">
              <Button variant="ghost" loading={remove.isPending} onClick={() => remove.mutate(member.userId)}>
                Confirmer le retrait
              </Button>
              <Button variant="link" size="s" type="button" onClick={() => setConfirm(false)}>Annuler</Button>
            </span>
          ) : (
            <Button variant="link-danger" size="s" type="button" onClick={() => setConfirm(true)}>
              Retirer l’accès
            </Button>
          )}
        </div>
      ) : (
        <span className="team-role">{member.role}</span>
      )}
      {error ? <div className="team-error"><ErrorBanner>{errorMessage(error)}</ErrorBanner></div> : null}
    </li>
  );
}

function RoleSelect({ value, onChange, id }: { value: ProjectRole; onChange: (r: ProjectRole) => void; id: string }) {
  return (
    <div className="zy-field">
      <label className="zy-field__label" htmlFor={id}>Rôle</label>
      <Select id={id} value={value} onChange={(e) => onChange(e.target.value as ProjectRole)}>
        {ROLES.map((r) => (
          <option key={r.value} value={r.value}>{r.label}</option>
        ))}
      </Select>
      <span className="zy-field__aide">{ROLES.find((r) => r.value === value)?.hint}</span>
    </div>
  );
}

function AddMember({ projectId, candidates }: { projectId: string; candidates: { userId: string; name: string; email: string }[] }) {
  const setRole = useSetMemberRole(projectId);
  const [userId, setUserId] = useState('');
  const [role, setRoleValue] = useState<ProjectRole>('Contributeur');
  if (candidates.length === 0) return null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!userId) return;
    try {
      await setRole.mutateAsync({ userId, role });
      setUserId('');
    } catch {
      /* rendu par le bandeau */
    }
  }

  return (
    <form className="team-form" onSubmit={submit} noValidate>
      <p className="opt-subtitle">Ajouter une personne de l’organisation</p>
      <div className="team-form-row">
        <div className="zy-field">
          <label className="zy-field__label" htmlFor="add-member">Personne</label>
          <Select id="add-member" value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">Choisir…</option>
            {candidates.map((c) => (
              <option key={c.userId} value={c.userId}>{c.name} — {c.email}</option>
            ))}
          </Select>
        </div>
        <RoleSelect id="add-role" value={role} onChange={setRoleValue} />
        <Button type="submit" variant="primary" loading={setRole.isPending} disabled={!userId}>
          Ajouter
        </Button>
      </div>
      {setRole.error ? <ErrorBanner>{errorMessage(setRole.error)}</ErrorBanner> : null}
    </form>
  );
}

// --- Invitation d'une personne sans compte ------------------------------------

function inviteLink(token: string): string {
  return `${window.location.origin}${hrefFor(`/invitation/${token}`)}`;
}

function inviteMailto(email: string, name: string | null, link: string, projectName: string): string {
  const subject = `Invitation au projet ${projectName} sur Zygosight`;
  const body = [
    `Bonjour${name ? ` ${name}` : ''},`,
    '',
    `Je vous invite à rejoindre le projet ${projectName} sur Zygosight.`,
    'Ce lien personnel vous permet de créer votre accès (il est valable 14 jours) :',
    '',
    link,
    '',
    'À bientôt !',
  ].join('\n');
  return `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function FreshInvite({ email, name, link, projectName, onDone }: {
  email: string; name: string | null; link: string; projectName: string; onDone: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="team-fresh" role="status">
      <p className="m0-note">
        Lien d’invitation pour <strong>{name ?? email}</strong>. <strong>Il ne sera plus affiché ensuite</strong> :
        envoyez-le maintenant. Valable 14 jours, utilisable une seule fois.
      </p>
      <input className="zy-input team-link" readOnly value={link} aria-label="Lien d’invitation" onFocus={(e) => e.target.select()} />
      <div className="team-fresh-actions">
        <a className={buttonClass('primary')} href={inviteMailto(email, name, link, projectName)}>Envoyer par e-mail</a>
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            navigator.clipboard.writeText(link).then(() => setCopied(true), () => setCopied(false));
          }}
        >
          {copied ? 'Lien copié' : 'Copier le lien'}
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>Fermer</Button>
      </div>
    </div>
  );
}

function InviteMember({ projectId, projectName }: { projectId: string; projectName: string }) {
  const invite = useInviteMember(projectId);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<ProjectRole>('Contributeur');
  const [fresh, setFresh] = useState<{ email: string; name: string | null; link: string } | null>(null);
  const [added, setAdded] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setAdded(null);
    try {
      const r = await invite.mutateAsync({ email: email.trim(), role, ...(name.trim() ? { name: name.trim() } : {}) });
      if (r.added) {
        setAdded(email.trim());
      } else if (r.invitation) {
        setFresh({ email: r.invitation.email, name: r.invitation.name, link: inviteLink(r.invitation.token) });
      }
      setEmail('');
      setName('');
    } catch {
      /* rendu par le bandeau */
    }
  }

  return (
    <form className="team-form" onSubmit={submit} noValidate>
      <p className="opt-subtitle">Inviter une nouvelle personne</p>
      <p className="m0-note">
        Elle recevra un lien personnel pour créer son accès, et n’accédera qu’à ce projet. Si l’adresse
        appartient déjà à votre organisation, la personne est simplement ajoutée.
      </p>
      <div className="team-form-row">
        <Field label="Nom" value={name} onChange={(e) => setName(e.target.value)} placeholder="Claire Martin" />
        <Field label="E-mail *" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="claire@entreprise.fr" />
        <RoleSelect id="invite-role" value={role} onChange={setRole} />
        <Button type="submit" variant="accent" loading={invite.isPending} disabled={!email.trim()}>
          Inviter
        </Button>
      </div>
      {invite.error ? <ErrorBanner>{errorMessage(invite.error)}</ErrorBanner> : null}
      {added ? <p className="m0-note" role="status">{added} fait déjà partie de l’organisation : ajouté au projet.</p> : null}
      {fresh ? <FreshInvite {...fresh} projectName={projectName} onDone={() => setFresh(null)} /> : null}
    </form>
  );
}

function AccessRequestRow({ projectId, request, onInvited }: {
  projectId: string;
  request: { id: string; email: string; name: string | null; createdAt: string };
  onInvited: (fresh: { email: string; name: string | null; link: string }) => void;
}) {
  const accept = useAcceptAccessRequest(projectId);
  const decline = useDeclineAccessRequest(projectId);
  const [role, setRole] = useState<ProjectRole>('Observateur');
  const [added, setAdded] = useState(false);

  return (
    <li className="team-invite">
      <div className="team-invite-head">
        <div>
          <p className="team-name">{request.name ?? request.email}</p>
          <p className="team-email">
            {request.name ? `${request.email} · ` : ''}demandé le {new Date(request.createdAt).toLocaleDateString('fr-FR')}
          </p>
        </div>
        <span className="team-status team-status-pending">À valider</span>
      </div>
      <div className="team-fresh-actions">
        <label className="sr-only" htmlFor={`req-role-${request.id}`}>Rôle accordé à {request.email}</label>
        <Select id={`req-role-${request.id}`} className="team-select" value={role} onChange={(e) => setRole(e.target.value as ProjectRole)}>
          {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </Select>
        <Button
          type="button"
          variant="primary"
          loading={accept.isPending}
          onClick={() => accept.mutate({ requestId: request.id, role }, {
            onSuccess: (r) => {
              if (r.invitation) onInvited({ email: r.invitation.email, name: r.invitation.name, link: inviteLink(r.invitation.token) });
              else setAdded(true);
            },
          })}
        >
          Accepter
        </Button>
        <Button type="button" variant="ghost" loading={decline.isPending} onClick={() => decline.mutate(request.id)}>
          Refuser
        </Button>
      </div>
      {added ? <p className="m0-note" role="status">Cette personne fait déjà partie de l’organisation : ajoutée au projet.</p> : null}
      {accept.error || decline.error ? <ErrorBanner>{errorMessage(accept.error ?? decline.error)}</ErrorBanner> : null}
    </li>
  );
}

const INVITE_STATUS: Record<TeamInvitation['status'], string> = {
  pending: 'En attente',
  expired: 'Expirée',
  revoked: 'Annulée',
};

function TeamInvitationRow({ projectId, projectName, invitation }: { projectId: string; projectName: string; invitation: TeamInvitation }) {
  const rotate = useRotateTeamInvitation(projectId);
  const revoke = useRevokeTeamInvitation(projectId);
  const [fresh, setFresh] = useState<string | null>(null);

  return (
    <li className="team-invite">
      <div className="team-invite-head">
        <div>
          <p className="team-name">{invitation.name ?? invitation.email}</p>
          <p className="team-email">{invitation.name ? `${invitation.email} · ` : ''}{invitation.role}</p>
        </div>
        <span className={`team-status team-status-${invitation.status}`}>{INVITE_STATUS[invitation.status]}</span>
      </div>
      <div className="team-fresh-actions">
        <Button
          type="button"
          variant="ghost"
          loading={rotate.isPending}
          onClick={() => rotate.mutate(invitation.id, { onSuccess: (inv) => setFresh(inviteLink(inv.token)) })}
        >
          Nouveau lien
        </Button>
        {invitation.status === 'pending' ? (
          <Button type="button" variant="ghost" loading={revoke.isPending} onClick={() => revoke.mutate(invitation.id)}>
            Annuler l’invitation
          </Button>
        ) : null}
      </div>
      {fresh ? (
        <FreshInvite email={invitation.email} name={invitation.name} link={fresh} projectName={projectName} onDone={() => setFresh(null)} />
      ) : null}
      {rotate.error || revoke.error ? <ErrorBanner>{errorMessage(rotate.error ?? revoke.error)}</ErrorBanner> : null}
    </li>
  );
}
